import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import type { Issue, BlockerEntry, BlockerRef, BlockerType } from '../types';
import { BLOCKER_TYPES, BLOCKER_LABEL, BLOCKER_HEAD, STATUS_LABELS } from '../types';
import { blockedSuggestions, entriesStillBlock, type BlockSuggestion } from '../blocked';
import { wouldCycle } from '../dependencies';
import { displayName, initials, type Profiles } from '../people';
import VendorTag from './VendorTag';
import { ageInDays } from '../card-age';
import Icon from './Icon';
import IssueText from './IssueText';
import InfoTip from './InfoTip';

/**
 * WHAT'S BLOCKING THIS — the surface for authoring a card's list of blockers (#456).
 *
 * A card used to carry ONE blocker in a single free-text box. Real work is held
 * up by several things at once — a change window AND a vendor quote AND a
 * sign-off — so a card now records a LIST, and the approved card face shows it.
 * This is the other half: where that list is made.
 *
 * Each entry is a {@link BlockerEntry}: a KIND (the nine "waiting on" kinds), a
 * REASON in words, and zero-or-more REFERENCES — each a card (shown with its LIVE
 * status, so a finished card's chip flips to Done on its own) or a person on the
 * board (who is told, in their feed, that this card waits on them). Everything
 * reuses the app's own blocker language (the `.qadd-ac` typeahead, the status
 * dot) so it reads native, not bolted on. Design signed off by Vinnie 10 Oct
 * (dev-preview/blocker-authoring/): tint every entry, one neutral "+ Add
 * blocker", short human kind-headers.
 *
 * It owns no persistence. It takes the current list and emits the next one; the
 * card modal holds the state and, on save, writes `blocker_refs` and keeps the
 * legacy scalar in step. `by` is stamped here (the editor) so the notify trigger
 * (migration 0159) can name who did the waiting.
 */

interface Props {
  /** The blockers as they stand (the modal's working copy). */
  entries: BlockerEntry[];
  /** Emit the next list — the modal replaces its working copy with this. */
  onChange: (next: BlockerEntry[]) => void;
  /** Every card on the board, for the typeahead and the live-status chips. */
  allIssues: Issue[];
  /** The people who can open this board, by address — the same seam the Assignee field uses. */
  assignable: string[];
  profiles: Profiles;
  /** The current user's address, stamped onto each entry so the feed can name them. */
  me: string;
  /** People already told (present in the SAVED list) — so a chip reads "Told", not "Will be told". */
  notifiedEmails: Set<string>;
  /** This card's id, so the typeahead never offers the card itself. */
  selfId?: string;
  /** True when the viewer may not change the block (the field is shown, read-only). */
  readOnly?: boolean;
}

/**
 * What the card modal calls on save to FLUSH a blocker the person built but did
 * not press "Add blocker" on — so picking a card then saving the card keeps the
 * blocker rather than silently dropping it. Returns the entries the modal should
 * write (the pending draft folded in), or null when there was nothing pending.
 */
export interface BlockerSectionHandle {
  flushPending: () => BlockerEntry[] | null;
}

/** The editor's working draft — a new entry ('new') or an existing one by index. */
interface Draft {
  at: number | 'new';
  type: BlockerType | '';
  refs: BlockerRef[];
  text: string;
}

const now = () => Date.now();

/** An entry says something worth keeping iff it names something or gives a reason. */
function entrySpeaks(d: { refs: BlockerRef[]; text: string }): boolean {
  return d.refs.length > 0 || d.text.trim() !== '';
}

const BlockerSection = forwardRef<BlockerSectionHandle, Props>(function BlockerSection({
  entries,
  onChange,
  allIssues,
  assignable,
  profiles,
  me,
  notifiedEmails,
  selfId,
  readOnly,
}: Props, ref) {
  const [draft, setDraft] = useState<Draft | null>(null);

  const hasAccess = useMemo(() => {
    const set = new Set(assignable.map((e) => e.toLowerCase()));
    return (email: string) => set.has(email.toLowerCase());
  }, [assignable]);

  const nameOfEmail = (email: string) => displayName(email, profiles) || email;

  // ── Editing lifecycle ──────────────────────────────────────────────────────
  function startPreset(kind: 'person' | 'item') {
    setDraft({ at: 'new', type: kind, refs: [], text: '' });
  }
  function startAdd() {
    setDraft({ at: 'new', type: '', refs: [], text: '' });
  }
  function startEdit(i: number) {
    const e = entries[i];
    setDraft({ at: i, type: e.type, refs: [...e.refs], text: e.text });
  }
  function cancelEdit() {
    setDraft(null);
  }
  // The next list a committable draft would produce — null when the draft is
  // empty or absent. Shared by the "Add blocker" button and the save-time flush,
  // so both fold the draft in exactly the same way.
  function nextFromDraft(): BlockerEntry[] | null {
    if (!draft || !draft.type || !entrySpeaks(draft)) return null;
    const prior = draft.at === 'new' ? undefined : entries[draft.at];
    const entry: BlockerEntry = {
      type: draft.type,
      text: draft.text.trim(),
      refs: draft.refs,
      // Keep the original clock on an edit (so "Told N days ago" holds steady);
      // a brand-new entry is stamped now.
      at: prior?.at ?? now(),
      // Who last recorded it — the actor the notify trigger names.
      by: me || prior?.by,
    };
    return draft.at === 'new'
      ? [...entries, entry]
      : entries.map((e, i) => (i === draft.at ? entry : e));
  }
  function commitDraft() {
    const next = nextFromDraft();
    if (!next) return;
    onChange(next);
    setDraft(null);
  }
  // Called by the card modal at save: a blocker the person built but did not
  // press "Add blocker" on is committed rather than lost. Returns the entries the
  // modal should write (so it need not wait for this component's state to settle),
  // or null when nothing was pending.
  useImperativeHandle(
    ref,
    () => ({
      flushPending() {
        const next = nextFromDraft();
        if (!next) return null;
        onChange(next);
        setDraft(null);
        return next;
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [draft, entries, me]
  );
  function removeEntry(i: number) {
    onChange(entries.filter((_, j) => j !== i));
    if (draft && draft.at === i) setDraft(null);
  }

  const count = entries.length;
  // The count is how many blockers are LISTED; the red flag is the claim the card
  // IS blocked. A linked card that has since finished still shows in the list (so
  // it can be seen and removed) but no longer makes the card blocked — so the flag
  // reads resolution, not presence, exactly as the read surfaces do (L165).
  const reallyBlocked = entriesStillBlock(entries, allIssues);

  return (
    <div className="write-field blk-sec">
      <div className="blk-sec-h">
        <span className="lbl">What&rsquo;s blocking this</span>
        {count > 0 && <span className="blk-count" aria-label={`${count} blocker${count === 1 ? '' : 's'}`}>{count}</span>}
        <InfoTip label="What's blocking this">
          List everything holding this card up. Link a card and it clears itself when that card
          finishes; name a person on the board and they&rsquo;re told, in their feed, that this
          card is waiting on them. A reason with nothing linked is fine too.
        </InfoTip>
        {reallyBlocked && (
          <span className="flag-loz blk-flag" aria-hidden="true">
            <Icon name="blocked" size={12} />
            Blocked
          </span>
        )}
      </div>

      {count === 0 && !draft && (
        <p className="hint blk-helper">
          Nothing&rsquo;s holding this up. Pick what it&rsquo;s waiting on — a linked card clears it
          when that card finishes, and a person on the board is told in their feed.
        </p>
      )}

      {(count > 0 || draft) && (
        <div className="blk-list">
          {entries.map((e, i) =>
            draft && draft.at === i ? (
              <Editor
                key={`edit-${i}`}
                draft={draft}
                setDraft={setDraft}
                commit={commitDraft}
                cancel={cancelEdit}
                allIssues={allIssues}
                assignable={assignable}
                profiles={profiles}
                selfId={selfId}
                nameOfEmail={nameOfEmail}
              />
            ) : (
              <EntryCard
                key={`entry-${i}`}
                entry={e}
                allIssues={allIssues}
                profiles={profiles}
                hasAccess={hasAccess}
                notifiedEmails={notifiedEmails}
                nameOfEmail={nameOfEmail}
                readOnly={readOnly}
                onEdit={() => startEdit(i)}
                onRemove={() => removeEntry(i)}
              />
            )
          )}
          {draft && draft.at === 'new' && (
            <Editor
              draft={draft}
              setDraft={setDraft}
              commit={commitDraft}
              cancel={cancelEdit}
              allIssues={allIssues}
              assignable={assignable}
              profiles={profiles}
              selfId={selfId}
              nameOfEmail={nameOfEmail}
            />
          )}
        </div>
      )}

      {!draft && !readOnly && (
        count > 0 ? (
          <button type="button" className="blk-add" onClick={startAdd}>
            <Icon name="plus" size={14} />
            Add blocker
          </button>
        ) : (
          <div className="blk-presets">
            <button type="button" className="blk-preset" onClick={() => startPreset('person')}>
              <Icon name="users" size={14} />
              Awaiting feedback from…
            </button>
            <button type="button" className="blk-preset" onClick={() => startPreset('item')}>
              <Icon name="link" size={14} />
              Blocked by a card…
            </button>
            <button type="button" className="blk-preset is-plain" onClick={startAdd}>
              <Icon name="plus" size={14} />
              Add a blocker
            </button>
          </div>
        )
      )}
    </div>
  );
});

export default BlockerSection;

// ── One blocker, as it reads in the list ──────────────────────────────────────
function EntryCard({
  entry,
  allIssues,
  profiles,
  hasAccess,
  notifiedEmails,
  nameOfEmail,
  readOnly,
  onEdit,
  onRemove,
}: {
  entry: BlockerEntry;
  allIssues: Issue[];
  profiles: Profiles;
  hasAccess: (email: string) => boolean;
  notifiedEmails: Set<string>;
  nameOfEmail: (email: string) => string;
  readOnly?: boolean;
  onEdit: () => void;
  onRemove: () => void;
}) {
  const cardRefs = entry.refs.filter((r) => r.kind === 'card');
  const personRefs = entry.refs.filter((r) => r.kind === 'person');
  return (
    <div className="blk-entry">
      <div className="blk-entry-head">
        <span className="blk-entry-kind">
          <Icon name={entry.type === 'item' ? 'link' : entry.type === 'person' ? 'users' : 'blocked'} size={14} />
          {BLOCKER_HEAD[entry.type]}
        </span>
        {!readOnly && (
          <span className="blk-entry-tools">
            <button type="button" className="blk-tool" aria-label="Edit this blocker" onClick={onEdit}>
              <Icon name="pencil" size={15} />
            </button>
            <button type="button" className="blk-tool" aria-label="Remove this blocker" onClick={onRemove}>
              <Icon name="close" size={15} />
            </button>
          </span>
        )}
      </div>

      {(cardRefs.length > 0 || personRefs.length > 0) && (
        <div className="blk-entry-refs">
          {cardRefs.map((r, i) => {
            const card = allIssues.find((c) => c.ref === r.ref);
            const done = card ? card.status === 'done' || card.status === 'wontdo' : false;
            return (
              <span key={`c-${i}`} className={`brc is-card${done ? ' is-done' : ''}`}>
                <span className="brc-dot" style={card ? { background: `var(--s-${card.status})` } : undefined} aria-hidden="true" />
                <span className="brc-ref">{r.ref}</span>
                {card && <span className="brc-ti">{card.title}</span>}
                <span className="brc-state">{card ? STATUS_LABELS[card.status] : 'Not on this board'}</span>
              </span>
            );
          })}
          {personRefs.map((r, i) => {
            // Only an EMAIL-identified person carries notify semantics — those
            // are the refs the new picker writes. A name-only ref is a card from
            // before the list existed (derived from the old scalar blocker): it
            // was never linked or notified, so it shows the name plainly, never a
            // "not notified" ghost that would read as "lost access".
            const isEmail = r.ref.includes('@');
            const access = isEmail && hasAccess(r.ref);
            const told = isEmail && notifiedEmails.has(r.ref.toLowerCase());
            const days = told && entry.at ? ageInDays(entry.at, Date.now()) : null;
            const state = !isEmail
              ? null
              : !access
                ? 'No longer on the board · not notified'
                : told
                  ? days != null && days > 0
                    ? `Told ${days} day${days === 1 ? '' : 's'} ago`
                    : 'Told'
                  : 'Will be told';
            return (
              <span key={`p-${i}`} className={`brc is-person${isEmail && !access ? ' is-ghost' : ''}`}>
                <span className="brc-av" aria-hidden="true">{initials(r.ref, profiles)}</span>
                <span className="brc-nm">{nameOfEmail(r.ref)}</span>
                <VendorTag who={r.ref} profiles={profiles} compact />
                {state && <span className="brc-state">{state}</span>}
              </span>
            );
          })}
        </div>
      )}

      {entry.text.trim() !== '' && (
        <div className="blk-entry-text">
          <IssueText text={entry.text} />
        </div>
      )}
    </div>
  );
}

// ── The in-place add / edit editor ────────────────────────────────────────────
function Editor({
  draft,
  setDraft,
  commit,
  cancel,
  allIssues,
  assignable,
  profiles,
  selfId,
  nameOfEmail,
}: {
  draft: Draft;
  setDraft: (d: Draft) => void;
  commit: () => void;
  cancel: () => void;
  allIssues: Issue[];
  assignable: string[];
  profiles: Profiles;
  selfId?: string;
  nameOfEmail: (email: string) => string;
}) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  // Set when a picked card would make the two cards wait on each other — a
  // finish-to-start cycle is unsatisfiable, so the pick is refused and this says
  // why. Cleared the moment the person types again or picks something that holds.
  const [cycleErr, setCycleErr] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // This card's own ref — the dependent gaining a blocker. A brand-new card has no
  // id yet, so no ref and nothing can already wait on it: the cycle check is moot.
  const selfRef = useMemo(() => allIssues.find((c) => c.id === selfId)?.ref, [allIssues, selfId]);

  const peopleNames = useMemo(() => assignable.map((e) => displayName(e, profiles)), [assignable, profiles]);
  const matches = useMemo(
    () => blockedSuggestions(q, allIssues, peopleNames, selfId),
    [q, allIssues, peopleNames, selfId]
  );
  const cur = matches.length ? Math.min(hi, matches.length - 1) : 0;
  // A name typed that matches nobody on the board — offered as plain text (decision #2).
  const offBoard = q.trim().length >= 2 && !matches.some((m) => m.kind === 'person');

  function addRef(m: BlockSuggestion) {
    let ref: BlockerRef | null = null;
    if (m.kind === 'card') {
      // REFUSE A CYCLE. Making this card wait on the picked card would close a
      // finish-to-start loop if the picked card already (transitively) waits on
      // this one — and a loop can never be satisfied (neither card could ever
      // start). The pure `wouldCycle` decides it over every board card; we just
      // decline to add the ref and say why.
      if (selfRef && wouldCycle(allIssues, selfRef, m.card.ref)) {
        setCycleErr('That would make the two cards wait on each other.');
        // Close the suggestions list, which is absolutely positioned over the
        // space below the input — left open it would paint right over this very
        // refusal message (and the reason box under it). The typed text stays so
        // the person can see what was refused and pick something else.
        setOpen(false);
        return;
      }
      ref = { kind: 'card', ref: m.card.ref, label: m.card.title };
    } else {
      const email = assignable.find((e) => displayName(e, profiles) === m.name);
      if (email) ref = { kind: 'person', ref: email, label: m.name };
    }
    if (!ref) return;
    const exists = draft.refs.some((r) => r.kind === ref!.kind && r.ref === ref!.ref);
    if (!exists) setDraft({ ...draft, refs: [...draft.refs, ref] });
    setCycleErr(null);
    setQ('');
    setOpen(false);
    inputRef.current?.focus();
  }
  function addAsText() {
    const t = q.trim();
    if (!t) return;
    setDraft({ ...draft, text: draft.text.trim() ? `${draft.text.trim()} ${t}` : t });
    setQ('');
    setOpen(false);
    inputRef.current?.focus();
  }
  function removeRef(j: number) {
    setDraft({ ...draft, refs: draft.refs.filter((_, i) => i !== j) });
  }

  const addedPeople = draft.refs.filter((r) => r.kind === 'person');
  const canCommit = !!draft.type && entrySpeaks(draft);

  return (
    <div className="blk-editor">
      <div className="write-field">
        <span className="lbl">
          What&rsquo;s it waiting on <span className="req" aria-hidden="true">*</span>
        </span>
        <select
          className="ctl"
          aria-label="What's it waiting on"
          value={draft.type}
          onChange={(e) => setDraft({ ...draft, type: e.target.value as BlockerType })}
        >
          <option value="">Choose one…</option>
          {BLOCKER_TYPES.map((t) => (
            <option key={t} value={t}>
              {BLOCKER_LABEL[t]}
            </option>
          ))}
        </select>
      </div>

      <div className="write-field">
        <span className="lbl">Name a card or a person on the board</span>
        {draft.refs.length > 0 && (
          <div className="ble-chosen">
            {draft.refs.map((r, j) => (
              <span key={j} className={`brc is-${r.kind} is-chosen`}>
                {r.kind === 'person' ? (
                  <>
                    <span className="brc-av" aria-hidden="true">{initials(r.ref, profiles)}</span>
                    <span className="brc-nm">{r.label || nameOfEmail(r.ref)}</span>
                    <span className="brc-state">Will be told</span>
                  </>
                ) : (
                  <>
                    <span className="brc-dot" aria-hidden="true" />
                    <span className="brc-ref">{r.ref}</span>
                    {r.label && <span className="brc-ti">{r.label}</span>}
                  </>
                )}
                <button type="button" className="brc-x" aria-label="Remove this reference" onClick={() => removeRef(j)}>
                  <Icon name="close" size={12} />
                </button>
              </span>
            ))}
          </div>
        )}
        <div className="blk-field">
          <input
            ref={inputRef}
            className="ctl"
            placeholder="Type a #number or a name — add as many as you need"
            aria-label="Name a card or a person"
            role="combobox"
            aria-expanded={open && (matches.length > 0 || offBoard)}
            autoComplete="off"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setOpen(true);
              setHi(0);
              setCycleErr(null);
            }}
            onFocus={() => setOpen(true)}
            // The ENTRY and its draft (refs + reason text) persist until the user
            // presses Add/Save or Cancel — that state lives in BlockerSection, NOT in
            // `open`, so it survives clicking into the reason box or the footer (the
            // #456 requirement). `open` governs only the suggestions dropdown, which
            // DOES close on focusout so it never paints over the reason box below it;
            // clicks on a suggestion are preserved by the list's onMouseDown
            // preventDefault, so the list still commits a pick. Also closes on Escape.
            onBlur={() => setOpen(false)}
            onKeyDown={(e) => {
              if (!open || (!matches.length && !offBoard)) {
                // No suggestions list showing: Escape backs out of the blocker ENTRY
                // itself (one step). Stop it here so the same press does not also reach
                // the card-level window Escape handler and exit edit mode in one go —
                // backing out one step at a time. A further Escape (entry gone) then
                // lets the card handler close edit mode as before.
                if (e.key === 'Escape') {
                  e.preventDefault();
                  e.stopPropagation();
                  cancel();
                }
                return;
              }
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setHi((h) => (h + 1) % matches.length);
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setHi((h) => (h - 1 + matches.length) % matches.length);
              } else if (e.key === 'Enter') {
                e.preventDefault();
                if (matches.length) addRef(matches[cur]);
                else if (offBoard) addAsText();
              } else if (e.key === 'Escape') {
                // First Escape closes ONLY the suggestions list; stop it reaching the
                // card-level window Escape handler so the entry and edit mode stay put.
                e.preventDefault();
                e.stopPropagation();
                setOpen(false);
              }
            }}
          />
          {open && (matches.length > 0 || offBoard) && (
            <ul className="qadd-ac blk-ac" role="listbox" aria-label="Cards and people" onMouseDown={(e) => e.preventDefault()}>
              <li className="qadd-ac-head" aria-hidden="true"><span>Blocked by</span></li>
              {matches.map((m, i) => (
                <li key={m.key} role="option" aria-selected={i === cur}>
                  <button type="button" className={`qadd-ac-row${i === cur ? ' is-hi' : ''}`} onClick={() => addRef(m)}>
                    {m.kind === 'card' ? (
                      <>
                        <span className="blk-dot" style={{ background: `var(--s-${m.card.status})` }} aria-hidden="true" />
                        <span className="qadd-ac-label">{m.card.ref} {m.card.title}</span>
                        <span className="qadd-ac-sub">{STATUS_LABELS[m.card.status]}</span>
                      </>
                    ) : (
                      <>
                        <Icon name="users" size={13} className="blk-personic" />
                        <span className="qadd-ac-label">{m.name}</span>
                        <span className="qadd-ac-sub">on the board</span>
                      </>
                    )}
                  </button>
                </li>
              ))}
              {offBoard && (
                <li role="option" aria-selected={false}>
                  <button type="button" className="qadd-ac-row is-offboard" onClick={addAsText}>
                    <Icon name="pencil" size={13} className="blk-personic" />
                    <span className="qadd-ac-label">{q.trim()}</span>
                    <span className="qadd-ac-sub">not on board · text</span>
                  </button>
                </li>
              )}
              <li className="qadd-ac-tip" aria-hidden="true">↑↓ to move · Enter to add · Esc to close</li>
            </ul>
          )}
        </div>
        {cycleErr && (
          <p className="hint ble-err" role="alert">
            <Icon name="blocked" size={13} />
            <span>{cycleErr}</span>
          </p>
        )}
      </div>

      <div className="write-field">
        <span className="lbl">What&rsquo;s awaited, or why</span>
        <textarea
          className="ctl"
          rows={2}
          aria-label="What's awaited, or why"
          value={draft.text}
          onChange={(e) => setDraft({ ...draft, text: e.target.value })}
        />
      </div>

      {addedPeople.length > 0 && (
        <p className="hint ble-notify">
          <Icon name="users" size={13} />
          <span>
            {addedPeople.map((r, i) => (
              <span key={i}>
                {i > 0 ? (i === addedPeople.length - 1 ? ' and ' : ', ') : ''}
                <b>{r.label || nameOfEmail(r.ref)}</b>
              </span>
            ))}
            {addedPeople.length === 1 ? ' will be told' : ' will be told'} in their feed that this card is
            waiting on them. People already linked aren&rsquo;t told again when you edit.
          </span>
        </p>
      )}

      <div className="ble-foot">
        <span className="ble-count">
          Linked cards and people added: <b>{draft.refs.length}</b>
        </span>
        <button type="button" className="ble-add" disabled={!canCommit} onClick={commit}>
          {draft.at === 'new' ? 'Add blocker' : 'Save blocker'}
        </button>
        <button type="button" className="ble-cancel" onClick={cancel}>Cancel</button>
      </div>
    </div>
  );
}
